// Modal helpers for ModalDialogContainer

"use strict";

import { onMounted, watch, type Ref } from "vue";
import { autoFocusElement } from "@/composables/useAutoFocus";

export type ModalContainerExposed = {
    close?: (forced?: boolean) => void;
    $el?: HTMLElement;
};

/**
 * Closes a modal container if it exposes a `close` method.
 * @param container Modal container instance from a template ref
 * @param forced When true, bypasses close guards and callbacks
 */
export function closeModalContainer(container: ModalContainerExposed | null | undefined, forced?: boolean): void {
    container?.close?.(forced);
}

/**
 * Returns the root element of a child component template ref.
 * @param componentRef Child component instance ref
 */
export function getComponentRootEl(componentRef: { $el?: HTMLElement } | null | undefined): HTMLElement | null | undefined {
    return componentRef?.$el;
}

/**
 * Composable that returns a function to close the bound modal container.
 * @param modalContainerRef Reactive reference to `ModalDialogContainer`
 * @returns Function that triggers the modal close animation
 */
export function useModalClose(modalContainerRef: Ref<ModalContainerExposed | null | undefined>) {
    /**
     * Closes the bound modal container.
     * @param forced When true, bypasses close guards and callbacks
     */
    return (forced?: boolean) => {
        closeModalContainer(modalContainerRef.value, forced);
    };
}

/**
 * Focuses the modal root element whenever it becomes visible.
 * @param display Visibility model ref
 * @param getRootEl Returns the modal root element
 */
export function useModalFocusOnOpen(display: Ref<boolean | undefined>, getRootEl: () => HTMLElement | null | undefined): void {
    const focus = () => {
        autoFocusElement(getRootEl());
    };

    watch(display, (isOpen) => {
        if (isOpen) {
            focus();
        }
    });

    onMounted(() => {
        if (display.value) {
            focus();
        }
    });
}

/**
 * Sets up close and focus-on-open behavior for a standard modal dialog.
 * @param display Visibility model ref
 * @param modalContainerRef Reactive reference to `ModalDialogContainer`
 * @returns Object with a `close` function for the bound modal container
 */
export function useModalDialog(display: Ref<boolean | undefined>, modalContainerRef: Ref<ModalContainerExposed | null | undefined>) {
    const closeModal = useModalClose(modalContainerRef);
    useModalFocusOnOpen(display, () => getComponentRootEl(modalContainerRef.value));
    return {
        /** Closes the modal; safe for template `@click` handlers. */
        close: () => closeModal(),
        /** Closes the modal; accepts `forced` for auth redirects and similar flows. */
        closeModal,
    };
}
